import { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import { checkSchemaReady } from '../helpers/schema.helpers';

export interface TabbedFormTab {
  name: string;
  schema: JSONSchema | null | undefined;
  initialValues: Record<string, any>;
}

interface UseTabbedFormOptions {
  tabs: TabbedFormTab[];
  onSave: (tabName: string, data: Record<string, any>) => Promise<void>;
  /** Override the initially selected tab. Defaults to tabs[0].name. */
  initialTab?: string;
  /** When true, switching tabs auto-saves the active tab if dirty and valid. Requires getActiveValues. */
  autoSave?: boolean;
  /** Returns current form values for the active tab. Used only when autoSave is true. */
  getActiveValues?: () => Record<string, any>;
}

interface UseTabbedFormReturn {
  activeTab: string | null;
  currentTabData: TabbedFormTab | null;
  tabReadiness: Record<string, boolean>;
  activeTabIsDirty: boolean;
  activeTabIsValid: boolean;
  handleTabChange: (tabName: string) => Promise<void>;
  setActiveTab: (tabName: string) => void;
  handleSubmit: (data: Record<string, any>) => Promise<void>;
  onDirtyChange: (isDirty: boolean) => void;
  onValidChange: (isValid: boolean) => void;
}

export const useTabbedForm = ({
  tabs,
  onSave,
  initialTab,
  autoSave = false,
  getActiveValues,
}: UseTabbedFormOptions): UseTabbedFormReturn => {
  const [activeTab, setActiveTab] = useState<string | null>(initialTab ?? tabs[0]?.name ?? null);
  const [localOverrides, setLocalOverrides] = useState<Record<string, Record<string, any>>>({});
  const [activeTabIsDirty, setActiveTabIsDirty] = useState(false);
  const [activeTabIsValid, setActiveTabIsValid] = useState(false);

  const stateRef = useRef({ activeTab, activeTabIsDirty, activeTabIsValid });
  stateRef.current = { activeTab, activeTabIsDirty, activeTabIsValid };

  const tabReadiness = useMemo(
    () =>
      Object.fromEntries(
        tabs.map((tab) => {
          // for the active dirty tab, reflect live form validity rather than saved state
          if (tab.name === activeTab && activeTabIsDirty) {
            return [tab.name, activeTabIsValid];
          }
          return [tab.name, checkSchemaReady(tab.schema, localOverrides[tab.name] ?? tab.initialValues)];
        }),
      ),
    [tabs, localOverrides, activeTab, activeTabIsDirty, activeTabIsValid],
  );

  const currentTabData = useMemo(() => tabs.find((tab) => tab.name === activeTab) ?? null, [tabs, activeTab]);

  const handleSubmit = useCallback(
    async (data: Record<string, any>) => {
      const { activeTab } = stateRef.current;
      if (!activeTab) {
        return;
      }
      await onSave(activeTab, data); // throws on error — prevents localOverrides update
      setLocalOverrides((prev) => ({ ...prev, [activeTab]: data }));
    },
    [onSave],
  );

  const handleTabChange = useCallback(
    async (tabName: string) => {
      const { activeTab, activeTabIsDirty, activeTabIsValid } = stateRef.current;
      if (autoSave && activeTab && activeTabIsDirty && activeTabIsValid && getActiveValues) {
        const data = getActiveValues();
        await onSave(activeTab, data);
        setLocalOverrides((prev) => ({ ...prev, [activeTab]: data }));
      }
      setActiveTabIsDirty(false);
      setActiveTabIsValid(false);
      setActiveTab(tabName);
    },
    [autoSave, getActiveValues, onSave],
  );

  const onDirtyChange = useCallback((isDirty: boolean) => setActiveTabIsDirty(isDirty), []);
  const onValidChange = useCallback((isValid: boolean) => setActiveTabIsValid(isValid), []);

  // initialize activeTab
  useEffect(() => {
    if (tabs.length === 0 || activeTab !== null) return;
    setActiveTab(tabs[0].name);
  }, [tabs, activeTab]);

  return {
    activeTab,
    currentTabData,
    tabReadiness,
    activeTabIsDirty,
    activeTabIsValid,
    handleTabChange,
    setActiveTab,
    handleSubmit,
    onDirtyChange,
    onValidChange,
  };
};
